(() => {
  if (window.forsterSalePopupInitialized) return;
  window.forsterSalePopupInitialized = true;

  const normalize = (value) => String(value || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

  const money = (cents) => {
    if (window.Shopify?.formatMoney) return window.Shopify.formatMoney(cents, window.money_format || '${{amount}}');
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: window.Shopify?.currency?.active || 'AUD' }).format(
      Number(cents || 0) / 100
    );
  };

  const currentState = (popup) => {
    const displayStart = Date.parse(popup.dataset.displayStart || popup.dataset.saleStart || '');
    const saleStart = Date.parse(popup.dataset.saleStart || '');
    const end = Date.parse(popup.dataset.saleEnd || '');
    const now = Date.now();
    const canDisplay = Number.isFinite(displayStart) && Number.isFinite(end) && now >= displayStart && now < end;
    const isLive = Number.isFinite(saleStart) && Number.isFinite(end) && now >= saleStart && now < end;

    return { canDisplay, isLive };
  };

  const timerTarget = (popup, isLive) =>
    Date.parse(isLive ? popup.dataset.saleEnd || '' : popup.dataset.saleStart || '');

  const formatDuration = (milliseconds) => {
    const totalSeconds = Math.max(0, Math.floor(milliseconds / 1000));
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    const pad = (value) => String(value).padStart(2, '0');

    return days > 0 ? `${days}d ${pad(hours)}:${pad(minutes)}:${pad(seconds)}` : `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  };

  const updateTimer = (popup, isLive) => {
    const target = timerTarget(popup, isLive);
    if (!Number.isFinite(target)) return;

    const remaining = target - Date.now();
    const label = popup.querySelector('[data-sale-timer-label]');
    const modalTimer = popup.querySelector('[data-sale-timer]');
    const inlineTimer = popup.querySelector('[data-sale-inline-timer]');
    const value = formatDuration(remaining);

    if (label) label.textContent = isLive ? 'Ends in' : 'Starts in';
    if (modalTimer) modalTimer.textContent = value;
    if (inlineTimer) inlineTimer.textContent = value;
  };

  const updateAnnouncementTimer = (bar, isLive) => {
    const target = Date.parse(isLive ? bar.dataset.saleEnd || '' : bar.dataset.saleStart || '');
    if (!Number.isFinite(target)) return;

    const timer = bar.querySelector('[data-sale-announcement-timer]');
    const title = bar.querySelector('[data-sale-announcement-title]');
    const value = formatDuration(target - Date.now());

    if (timer) timer.textContent = value;
    if (title) title.textContent = isLive ? '3 Million View SALE !!' : '3 Million View SALE !!';
  };

  const keywords = (value) => String(value || '').split('|').map(normalize).filter(Boolean);

  const variantText = (variant) => normalize([variant.title, ...(variant.options || [])].filter(Boolean).join(' '));

  const matchingVariants = (popup, variants) => {
    const colors = keywords(popup.dataset.saleColors);
    const noEngravingKeywords = keywords(popup.dataset.saleKeywords);

    return variants.filter((variant) => {
      const text = variantText(variant);
      const hasColor = colors.some((color) => text.includes(color));
      const hasNoEngraving = noEngravingKeywords.some((keyword) => text.includes(keyword));
      return variant.available && hasColor && hasNoEngraving;
    });
  };

  const getVariants = (popup) => {
    const script = popup.closest('form')?.querySelector('[data-engraving-variants]');
    try {
      return JSON.parse(script?.textContent || '[]');
    } catch (error) {
      return [];
    }
  };

  const getForm = (popup) => popup.closest('form[data-type="add-to-cart-form"]');

  const getModal = (popup) => {
    if (!popup.dataset.salePopupId) popup.dataset.salePopupId = `sale-popup-${Math.random().toString(36).slice(2)}`;

    let modal = popup.querySelector('[data-sale-modal]');
    if (!modal) modal = document.querySelector(`[data-sale-modal][data-sale-owner="${popup.dataset.salePopupId}"]`);
    if (!modal) return null;

    modal.dataset.saleOwner = popup.dataset.salePopupId;
    if (modal.parentElement?.matches('[data-sale-popup]')) {
      popup.append(document.createComment('forster sale modal moved to body'));
      document.body.append(modal);
    }

    return modal;
  };

  const openModal = (popup) => {
    const modal = getModal(popup);
    if (!modal) return;
    modal.hidden = false;
    document.body.classList.add('sale-modal-open');
    window.setTimeout(() => modal.querySelector('button, [href], input')?.focus(), 50);
  };

  const closeModal = (popup) => {
    const modal = getModal(popup);
    if (modal) modal.hidden = true;
    document.body.classList.remove('sale-modal-open');
  };

  const setVariant = (form, variantId) => {
    const idInput = form.querySelector('[name="id"]');
    if (!idInput) return;
    idInput.value = variantId;
    idInput.dispatchEvent(new Event('change', { bubbles: true }));
  };

  const setCopy = (popup, isLive) => {
    const badge = popup.querySelector('[data-sale-badge]');
    const teaserText = popup.querySelector('[data-sale-teaser-text]');
    const modalText = popup.querySelector('[data-sale-modal-text]');

    if (badge) badge.textContent = isLive ? '3 day sale' : 'Starts tomorrow';
    if (teaserText) {
      teaserText.textContent = isLive
        ? 'Black and blue available for a limited time.'
        : 'Black and blue go live tomorrow for a limited time.';
    }
    if (modalText) {
      modalText.textContent = isLive
        ? 'Choose the black or blue no-engraving version while this short offer is live.'
        : 'Black and blue no-engraving versions go live tomorrow for 3 days only.';
    }
  };

  const renderVariants = (popup, variants, isLive) => {
    const container = popup.querySelector('[data-sale-variants]');
    const empty = popup.querySelector('[data-sale-empty]');
    if (!container) return;

    container.replaceChildren();
    empty.hidden = variants.length > 0;

    variants.forEach((variant) => {
      const item = document.createElement('div');
      item.className = 'sale-modal__variant';
      item.innerHTML = `
        <div>
          <div class="sale-modal__variant-title">${variant.public_title || variant.title}</div>
          <div class="sale-modal__variant-price">${money(variant.price)}</div>
        </div>
        <button type="button" class="button button--primary" data-sale-variant-id="${variant.id}" ${isLive ? '' : 'disabled'}>
          ${isLive ? 'Choose this option' : 'Starts tomorrow'}
        </button>
      `;
      container.append(item);
    });
  };

  const setupPopup = (popup) => {
    const { canDisplay, isLive } = currentState(popup);
    if (!canDisplay) return;

    const variants = matchingVariants(popup, getVariants(popup));
    setCopy(popup, isLive);
    renderVariants(popup, variants, isLive);
    updateTimer(popup, isLive);
    window.setInterval(() => updateTimer(popup, currentState(popup).isLive), 1000);

    popup.hidden = false;

    if (!window.sessionStorage.getItem('forsterNoEngravingSaleSeen')) {
      window.sessionStorage.setItem('forsterNoEngravingSaleSeen', 'true');
      window.setTimeout(() => openModal(popup), 700);
    }
  };

  const setupAnnouncement = (bar) => {
    const { canDisplay, isLive } = currentState(bar);
    if (!canDisplay) return;

    if (document.querySelector('[data-sale-popup]')) {
      bar.setAttribute('href', window.location.pathname);
    }

    updateAnnouncementTimer(bar, isLive);
    window.setInterval(() => updateAnnouncementTimer(bar, currentState(bar).isLive), 1000);
    bar.hidden = false;
    document.body.classList.add('forster-sale-announcement-visible');
  };

  document.addEventListener('click', (event) => {
    const openButton = event.target.closest('[data-sale-open]');
    if (openButton) {
      const popup = openButton.closest('[data-sale-popup]');
      if (popup) openModal(popup);
      return;
    }

    const closeButton = event.target.closest('[data-sale-close]');
    if (closeButton) {
      const owner = closeButton.closest('[data-sale-modal]')?.dataset.saleOwner;
      const popup = owner
        ? document.querySelector(`[data-sale-popup][data-sale-popup-id="${owner}"]`)
        : closeButton.closest('[data-sale-popup]');
      if (popup) closeModal(popup);
      return;
    }

    const variantButton = event.target.closest('[data-sale-variant-id]');
    if (!variantButton) return;
    if (variantButton.disabled) return;

    const owner = variantButton.closest('[data-sale-modal]')?.dataset.saleOwner;
    const popup = owner
      ? document.querySelector(`[data-sale-popup][data-sale-popup-id="${owner}"]`)
      : variantButton.closest('[data-sale-popup]');
    if (!popup) return;
    const form = getForm(popup);
    if (!form) return;

    setVariant(form, variantButton.dataset.saleVariantId);
    closeModal(popup);

    if (form.requestSubmit) {
      form.requestSubmit();
    } else {
      form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    document.querySelectorAll('[data-sale-modal]:not([hidden])').forEach((modal) => {
      const popup = modal.closest('[data-sale-popup]');
      if (popup) closeModal(popup);
    });
  });

  document.querySelectorAll('[data-sale-popup]').forEach(setupPopup);
  document.querySelectorAll('[data-sale-announcement]').forEach(setupAnnouncement);
})();
